import React from "react";
import { saveAs } from "file-saver";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import FileUploadRenderer from "../renderers/v2/FileUploadRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import { setVariableMentions, validationClear } from "../../redux/actions";
import { getFile, clearFile, uploadFile } from "../../redux/thunks/file";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, ThunkDispatchProp {}

interface State {
    fileName: string;
    mimeType: string;
    size: number;
    fileBlob: Blob;
    status: string;
    progress: number;
    validationResult?: ValidationResultModel;
    isValid: boolean;
    errorMessage: string;
    statusAnnouncement: string;
    lastAnnouncedProgressStep: number;
}

function formatTranslated(template: string, ...args: string[]): string {
    if (!template) return template;
    return template.replace(/\{(\d+)\}/g, (match, idx) => {
        const i = Number(idx);
        return args[i] !== undefined ? args[i] : match;
    });
}

export class FileUploadControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            fileName: "",
            size: 0,
            mimeType: "",
            fileBlob: null,
            status: "",
            progress: 0,
            isValid: true,
            errorMessage: "",
            statusAnnouncement: "",
            lastAnnouncedProgressStep: 0,
        };
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        this.updateState(props);
        const [variable] = props.question.variables;
        const mention = this.getMention(props, variable.name);
        if (mention) {
            const stateObject = JSON.parse(mention.value);
            props
                .dispatch(getFile(variable.name, stateObject.fileName, stateObject.mimeType))
                .then((data) => {
                    if (data) {
                        this.setState((state) => ({ ...state, fileBlob: data }));
                    } else {
                        this.onClearFile();
                    }
                });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.updateState(nextProps);
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        // Announce progress thresholds (50%, 100%) once each, only during an active upload.
        const prev = prevState.progress || 0;
        const curr = this.state.progress || 0;
        if (this.state.fileName && this.state.isValid && curr > prev) {
            const step = curr >= 1 ? 100 : curr >= 0.5 ? 50 : 0;
            if (step > this.state.lastAnnouncedProgressStep) {
                const key = step === 100 ? "FileUploadComplete" : "FileUploadProgress";
                const template =
                    translate(this.props.questionnaire, key, this.props.language) || "";
                const message =
                    step === 100 ? template : formatTranslated(template, String(step));
                this.setState({
                    statusAnnouncement: message,
                    lastAnnouncedProgressStep: step,
                });
            }
        }
    }

    updateState(props: Props) {
        this.setState((s) => {
            const newState = { ...s };
            const [variable] = props.question.variables;

            const mention = this.getMention(props, variable.name);
            if (mention) {
                const stateObject = JSON.parse(mention.value);
                newState.fileName = stateObject.fileName;
                newState.size = stateObject.size;
                newState.mimeType = stateObject.mimeType;
            }

            const file = this.getFile(props, variable.name);
            if (file) {
                newState.status = file.status;
                newState.progress = file.progress;
            }
            return newState;
        });
    }

    onFileOpen = () => {
        const { fileBlob, fileName } = this.state;
        if (fileBlob) saveAs(fileBlob, fileName);
    };

    onClearFile = () => {
        this.clearFileSelection();
    };

    onFileSelection = (selectedFile: File) => {
        const { name, size, type } = selectedFile;
        const { question, matrix, dispatch, questionnaire, language } = this.props;
        const [variable] = question.variables;
        const selectedTemplate =
            translate(questionnaire, "FileUploadFileSelected", language) || "";
        this.setState((state) => ({
            ...state,
            fileName: name,
            size,
            mimeType: type,
            fileBlob: new Blob([selectedFile]),
            status: "",
            progress: 0,
            isValid: true,
            errorMessage: "",
            statusAnnouncement: formatTranslated(selectedTemplate, name),
            lastAnnouncedProgressStep: 0,
        }));

        dispatch(setVariableMentions(variable.name, (mentions) => mentions.clear()));

        dispatch(validationClear());

        if (size > 0) {
            const addMention = () => {
                dispatch(
                    setVariableMentions(variable.name, (mentions) =>
                        mentions.add(
                            JSON.stringify({
                                fileName: name,
                                size,
                                mimeType: type,
                            }),
                        ),
                    ),
                );
            };

            if (!this.isFileSizeValid(selectedFile.size)) {
                this.setInvalidState("FileUploadErrorSize");
                addMention();
            } else if (!this.isMimeTypeValid(selectedFile.name)) {
                this.setInvalidState("FileUploadErrorType");
                addMention();
            } else {
                dispatch(
                    uploadFile(variable.name, matrix, selectedFile, (serverErrorKey) => {
                        if (serverErrorKey) {
                            this.setInvalidState(serverErrorKey);
                        } else {
                            addMention();
                        }
                    }),
                );
            }
        }
    };

    setInvalidState = (errorKey) => {
        const { questionnaire, language, question } = this.props;
        let message = translate(questionnaire, errorKey, language);
        if (errorKey === "FileUploadErrorSize") {
            const maxSize = parseFloat(getSettingString(question, "MaxFileSize"));
            if (!isNaN(maxSize)) {
                message = formatTranslated(message, String(maxSize));
            }
        } else if (errorKey === "FileUploadErrorType") {
            const acceptedTypes = getSettingString(question, "AcceptedFileTypes") || "";
            message = formatTranslated(message, acceptedTypes);
        }
        this.setState((state) => ({
            ...state,
            isValid: false,
            errorMessage: message,
            statusAnnouncement: "",
            lastAnnouncedProgressStep: 0,
        }));
    };

    clearFileSelection = () => {
        const [variable] = this.props.question.variables;
        const { questionnaire, language } = this.props;
        const removedAnnouncement =
            translate(questionnaire, "FileUploadFileRemoved", language) || "";
        this.props.dispatch(clearFile(variable.name, this.props.matrix));
        this.setState({
            fileName: "",
            size: 0,
            mimeType: "",
            fileBlob: null,
            statusAnnouncement: removedAnnouncement,
            lastAnnouncedProgressStep: 0,
            isValid: true,
            errorMessage: "",
        });
        this.setMentions(this.props, variable.name, (m) => m.clear());
    };

    renderQuestion() {
        const {
            fileName,
            size,
            mimeType,
            status,
            fileBlob,
            progress,
            isValid,
            errorMessage,
            statusAnnouncement,
        } = this.state;
        const { theme, language, question, questionnaire } = this.props;
        const [variable] = this.props.question.variables;
        const browseFilesLabel = translate(theme, "BrowseFiles", language) || "BrowseFiles";
        const dragFilesLabel = translate(theme, "DragDropFiles", language) || "DragDropFiles";
        const removeButtonLabel =
            translate(questionnaire, "FileUploadRemoveButton", language) || "";
        const openButtonLabel =
            translate(questionnaire, "FileUploadOpenButton", language) || "";

        const acceptedTypesSetting = getSettingString(question, "AcceptedFileTypes") || "";
        const maxSizeSetting = getSettingString(question, "MaxFileSize") || "";
        const acceptedTypesTemplate =
            translate(questionnaire, "FileUploadAcceptedTypes", language) || "";
        const maxSizeTemplate = translate(questionnaire, "FileUploadMaxSize", language) || "";
        const acceptedTypesText = acceptedTypesSetting
            ? formatTranslated(acceptedTypesTemplate, acceptedTypesSetting)
            : "";
        const maxSizeText =
            maxSizeSetting && parseFloat(maxSizeSetting) > 0
                ? formatTranslated(maxSizeTemplate, maxSizeSetting)
                : "";
        const isRequired = this.isAnswerRequired(questionnaire, question);

        return (
            <FileUploadRenderer
                {...getRendererProps(this.props)}
                variableName={variable.name}
                questionElementId={question.elementId}
                fileName={fileName}
                fileSize={size}
                fileType={fileName.split(".").pop()}
                fileMime={mimeType}
                fileStatus={status}
                fileBlob={fileBlob}
                fileProgress={progress}
                onClearFile={this.onClearFile}
                onFileSelection={this.onFileSelection}
                onFileOpen={this.onFileOpen}
                browseFilesLabel={browseFilesLabel}
                dragFilesLabel={dragFilesLabel}
                isValid={isValid}
                isRequired={isRequired}
                uploadErrorMessage={errorMessage}
                acceptedTypesText={acceptedTypesText}
                maxSizeText={maxSizeText}
                removeButtonLabel={removeButtonLabel}
                openButtonLabel={openButtonLabel}
                statusAnnouncement={statusAnnouncement}
            />
        );
    }

    validate(): ValidationResultModel {
        const { question, useClientValidation, questionnaire } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const [variable] = question.variables;
        const required = this.isAnswerRequired(questionnaire, question);
        const mention = this.getMention(this.props, variable.name);

        if (required && !mention)
            return new ValidationBuilder().fail(variable.name, "IncompleteAnswer").build();

        if (mention) {
            const mentionValueObject = JSON.parse(mention.value);

            if (!this.isMimeTypeValid(mentionValueObject.fileName))
                return new ValidationBuilder().fail(variable.name, "InvalidFileType").build();

            if (!this.isFileSizeValid(mentionValueObject.size)) {
                const maxSize = parseFloat(getSettingString(question, "MaxFileSize")) * 1024;
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidFileSize", { max: maxSize.toString() })
                    .build();
            }
        }
        return new ValidationBuilder().build();
    }

    isFileSizeValid(size: number): boolean {
        const maxSize = parseFloat(getSettingString(this.props.question, "MaxFileSize")) * 1024;
        if (maxSize && maxSize > 0 && size > maxSize) {
            return false;
        }

        return true;
    }

    isMimeTypeValid(fileName: string): boolean {
        const acceptsMimeTypes = getSettingString(this.props.question, "AcceptedFileTypes");
        let acceptsMimeType: string[] = [];
        if (acceptsMimeTypes && acceptsMimeTypes.length > 0) {
            acceptsMimeType = acceptsMimeTypes.split(",").map((m) => m.trim().toLowerCase());
        }
        const fileExtension = fileName.split(".").pop()?.toLowerCase();

        if (acceptsMimeType.length === 0) {
            return true;
        }

        return fileName !== undefined && acceptsMimeType.some((m) => m.includes(fileExtension));
    }
}
